import { Question } from './config'
import { Asset } from 'contentful'
import { BLOCKS, Document, TopLevelBlock, Text } from '@contentful/rich-text-types'
import { StructuredSchema } from '~/components/StructuredSchema'
import { FlexContainer, FlexItem } from '~/components/Grid'
import { RichText } from '~/components/RichText'
import { Faq } from './Faq'
import { useMemo } from 'react'
import type { Options as ContentfulRichTextOptions } from '@contentful/rich-text-react-renderer'
import { createOptimizedContentfulImage, getContentfulAsset } from '~/libs/contentful/helpers'

type FaqBlockProps = {
  questions?: Question[]
  title?: string
  image?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>
  additionalNotes?: Document
  richTextBaseOptions?: ContentfulRichTextOptions
  blockId?: string
}

export const FaqBlock = ({
  questions,
  title,
  image,
  additionalNotes,
  richTextBaseOptions,
  blockId,
}: FaqBlockProps) => {
  // TODO move this to props
  const baseOptions = useMemo<ContentfulRichTextOptions>(
    () => ({
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <p className="text-neutral-50 text-s max-md:w-auto max-md:text-center">{children}</p>
        ),
      },
      ...richTextBaseOptions?.renderNode,
    }),
    [],
  )

  const imageEntry = getContentfulAsset(image)

  return (
    <FlexContainer
      className="mlb-24 max-lg:mlb-15 max-md:mlb-10 !mt-0"
      data-block-id={blockId || 'faq_block'}
    >
      <FlexItem desktop={12}>
        <div className="w-auto m-auto max-md:pb-10 max-lg:mb-15 mb-24 border-t border-neutral-20"></div>

        <div className="flex gap-50 max-w-[1120px] max-lg:flex-col max-lg:gap-6 max-lg:py-1 max-lg:px-8 max-lg:text-center max-md:m-0 max-md:mt-3 max-md:h-auto max-md:w-auto max-md:gap-5 max-md:items-center">
          <h2 className="text-xl font-tt_talent max-lg:text-center max-lg:order-1 border-t-neutral-20 max-lg:flex max-lg:flex-col text-neutral-100">
            {title}
          </h2>

          {imageEntry && (
            <img
              className="max-lg:order-none ml-auto max-lg:m-auto max-lg:text-center max-lg:gap-25 w-[256px] h-[120px] max-lg:w-[172px] max-lg:h-20 max-md:w-[146px] max-md:h-17"
              alt=""
              src={createOptimizedContentfulImage({
                file: (imageEntry as Asset<undefined, string>).fields.file!,
              })}
            />
          )}
        </div>
        <div className="max-lg:flex max-lg:flex-col max-lg:justify-center max-lg:items-center max-lg:mlb-8 max-md:mx-auto">
          {questions?.map((ele) => {
            return <Faq key={ele.question} question={ele.question} answer={ele.answer} />
          })}
          <div className="mt-10 text-s max-md:mb-3 max-md:block">
            <RichText
              doc={additionalNotes?.content[0] as unknown as Document}
              options={baseOptions}
            />
          </div>
        </div>
      </FlexItem>
      <StructuredSchema
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: questions?.map((q) => {
            const answer =
              (((q.answer as unknown as Document).content[0] as TopLevelBlock).content[0] as Text)
                .value || ''
            return {
              '@type': 'Question',
              name: q.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: answer,
              },
            }
          }),
        }}
      />
    </FlexContainer>
  )
}
